<template>
  <div class="rrd-recognition-badges" role="list" aria-label="Recognition status by economy">
    <span
      v-for="item in normalized"
      :key="item.countryCode"
      class="rrd-recognition-chip"
      :class="statusClass(item.status)"
      role="listitem"
      :title="tooltipText(item)"
    >
      <span class="rrd-code">{{ item.countryCode }}</span>

      <span class="rrd-sep" aria-hidden="true">—</span>

      <span class="rrd-text">
        {{ statusText(item.status) }}
        <span v-if="showCycle && item.cycle" class="rrd-cycle">
          ({{ item.cycle }})
        </span>
      </span>

      <span class="rrd-icon" aria-hidden="true">{{ statusIcon(item.status) }}</span>
    </span>

    <span v-if="!normalized.length" class="rrd-empty text-muted small">
      —
    </span>
  </div>
</template>

<script>
export default {
  name: "RecognitionBadge",
  props: {
    results: { type: Array, default: () => [] },
    showCycle: { type: Boolean, default: true },
    unknownLabel: { type: String, default: "Unknown" },
  },

  computed: {
    normalized() {
      const src = Array.isArray(this.results) ? this.results : [];

      const items = src
        .filter(Boolean)
        .map((r) => {
          const code =
            r.countryCode ||
            r.country_code ||
            (r.country && (r.country.key || r.country.code)) ||
            r.country;

          const status = r.status || r.recognition_status || r.result || "UNKNOWN";

          return {
            countryCode: code ? String(code).trim().toUpperCase() : null,
            status: String(status).trim().toUpperCase(),
            cycle: r.cycle || null,
            note: r.note || null,
          };
        })
        .filter((x) => x.countryCode);

      // de-dupe by countryCode (zadrži prvi)
      const seen = new Set();
      const deduped = [];
      for (const it of items) {
        if (!seen.has(it.countryCode)) {
          seen.add(it.countryCode);
          deduped.push(it);
        }
      }

      // stabilno sortiranje po kodu zemlje
      return deduped.sort((a, b) => a.countryCode.localeCompare(b.countryCode));
    },
  },

  methods: {
    statusText(status) {
      // naziv koji ti "mora" da stoji u UI
      if (status === "FULL") return "Fully recognized";
      if (status === "PARTIAL") return "Partially recognized";
      if (status === "CONDITIONAL") return "Conditionally recognized";
      if (status === "PENDING") return "Pending";
      if (status === "NOT") return "Not recognized";
      return this.unknownLabel;
    },

    statusIcon(status) {
      if (status === "FULL") return "✓";
      if (status === "PARTIAL" || status === "CONDITIONAL") return "!";
      if (status === "PENDING") return "…";
      if (status === "NOT") return "×";
      return "•";
    },

    statusClass(status) {
      if (status === "FULL") return "is-full";
      if (status === "PARTIAL" || status === "CONDITIONAL") return "is-partial";
      if (status === "PENDING") return "is-pending";
      if (status === "NOT") return "is-not";
      return "is-unknown";
    },

    tooltipText(item) {
      const cycle = item.cycle ? ` (${item.cycle})` : "";
      const note = item.note ? ` — ${item.note}` : "";
      return `${item.countryCode}: ${this.statusText(item.status)}${cycle}${note}`;
    },
  },
};
</script>

<style scoped>
.rrd-recognition-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
}

/* Chip */
.rrd-recognition-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 12px;
  line-height: 1;
  border: 1px solid rgba(0, 0, 0, 0.12);
  background: #fff;
  user-select: none;
  white-space: nowrap;
}

.rrd-code {
  font-weight: 700;
  letter-spacing: 0.3px;
}

.rrd-sep {
  opacity: 0.6;
}

.rrd-text {
  font-weight: 600;
}

.rrd-cycle {
  font-weight: 600;
  opacity: 0.8;
}

.rrd-icon {
  font-size: 13px;
  margin-left: 2px;
}

/* Status styles */
.is-full {
  border-color: rgba(25, 135, 84, 0.35);
  background: rgba(25, 135, 84, 0.08);
}

.is-partial {
  border-color: rgba(255, 193, 7, 0.45);
  background: rgba(255, 193, 7, 0.12);
}

.is-pending {
  border-color: rgba(13, 110, 253, 0.25);
  background: rgba(13, 110, 253, 0.06);
}

.is-not {
  border-color: rgba(220, 53, 69, 0.35);
  background: rgba(220, 53, 69, 0.08);
}

.is-unknown {
  border-color: rgba(108, 117, 125, 0.35);
  background: rgba(108, 117, 125, 0.10);
}
</style>
